import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import { useGetTree } from "@react-admin/ra-tree";

import { useController, useFormContext } from "react-hook-form";
import dataProvider from "server/dataProvider";
import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  useSimpleFormIteratorItem,
} from "react-admin";
import TreeSelect from "features/TreeSelect";
import { employeeRequests } from "api/employeeApi";
import { administrationRequests } from "api/administrationApi";
import { accountRequests } from "api/accountApi";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import { getEmployeeProfiles, getPermissions } from "auth/security/securityUtils";

const NodeItem = ({
  name,
  structuralUnitId,
  setStructuralUnitId,
  defaultValue = "",
  source,
  reference,
  label = "Структура",
  ...rest
}) => {
  const { control, watch, setValue } = useFormContext();
  const nodeEmployees = watch("nodeEmployees");
  const { index } = useSimpleFormIteratorItem();

  const { field } = useController({
    name,
    control,
    defaultValue,
  });

  const [displayValues, setDisplayValues] = useState({});

  // Per-position roles only matter where position-based profiles are enabled (AccountService setting).
  const positionRolesEnabled = getEmployeeProfiles()?.enabled === true;
  const canAttachRoles = checkPermission(getPermissions(), permissions.canAttachRolesToEmployees);

  const [anchorEl, setAnchorEl] = useState(null);
  const [nodes, setNodes] = useState([]);
  const open = Boolean(anchorEl);

  const currentItem = nodeEmployees ? nodeEmployees[index] : null;
  const currentPositionIds = nodes?.find(item => item.id === currentItem.nodeId)?.positionIds || null;
  const currentNodeId = currentItem ? nodeEmployees?.[index]?.nodeId : null;
  const rowStructuralUnit = structuralUnitId?.[index] || nodeEmployees?.[index]?.structuralUnitId;

  const { data } = useGetTree(`${reference}?StructuralUnitId=${rowStructuralUnit}`);

  useEffect(() => {
    dataProvider
      .getList(reference, {
        pagination: { page: 1, perPage: 100 },
        sort: { field: "id", order: "ASC" },
      })
      .then(({ data }) => {
        setNodes(data);

        const name = findNodeName(data, currentNodeId);

        setDisplayValues(prev => ({ ...prev, [index]: name || "" }));
      });
  }, [reference]);

  // recursive search
  const findNodeName = (nodes, id) => {
    for (const node of nodes) {
      if (node.id === id) return node.name;
      if (node.children) {
        const found = findNodeName(node.children, id);
        if (found) return found;
      }
    }
    return "";
  };

  const handleSelect = selectedId => {
    field.onChange(selectedId);

    const name = findNodeName(nodes, selectedId);
    setDisplayValues(prev => ({ ...prev, [index]: name || "" }));

    setAnchorEl(null);
  };

  return (
    <>
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="structuralUnitId"
          reference={administrationRequests.getStructuralUnitsSelect()}
          filter={{ filterByUser: true }}
        >
          <SelectInput
            label={"Структурни единици"}
            multiple={false}
            onChange={e => {
              if (!e) return;
              setStructuralUnitId(prev => ({ ...prev, [index]: e.target.value || "" }));
              setValue(`nodeEmployees.${index}.nodeId`, null);
              setValue(`nodeEmployees.${index}.positions`, []);
              handleSelect(null);
            }}
          />
        </ReferenceInput>
      </Grid>
      {rowStructuralUnit ? (
        <Grid
          item
          xs={6}
        >
          <TreeSelect
            label={label}
            open={open}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            handleSelect={handleSelect}
            data={data}
            source={source}
            displayValues={displayValues[index]}
          />
        </Grid>
      ) : null}
      <ArrayInput
        source="positions"
        label="Позиции в структурата"
      >
        <SimpleFormIterator>
          <Grid container>
            <Grid
              item
              xs={12}
              md={6}
            >
              <ReferenceInput
                source="positionId"
                reference={employeeRequests.getPositionsSelect()}
                filter={{ filterByUser: true, ids: currentPositionIds }}
                allowEmpty
                perPage={100}
              >
                <AutocompleteInput
                  fullWidth={false}
                  label="Позиция"
                  filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                  validate={required()}
                />
              </ReferenceInput>
            </Grid>
          </Grid>

          <BooleanInput
            source="isManager"
            label="Ръководител"
          />
          <BooleanInput
            source="isPartTime"
            label="Хоноруван"
          />
          {positionRolesEnabled && (
            <ReferenceArrayInput
              source="roleIds"
              reference={accountRequests.getRolesSelect()}
            >
              <AutocompleteArrayInput
                optionText="name"
                label="Роли за позицията"
                readOnly={!canAttachRoles}
                filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                fullWidth
                disableCloseOnSelect
              />
            </ReferenceArrayInput>
          )}
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

export default NodeItem;
